import { useState, useEffect, useCallback } from 'react';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import {
  DollarSign, CreditCard, Banknote, Search, Calendar, Download,
  RefreshCw, ChevronLeft, ChevronRight, Eye, Receipt,
  ArrowUpRight, ArrowDownRight, TrendingUp, Store, ChevronDown, X
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';

import { API } from '@/config/api';

// Format currency
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2
  }).format(amount || 0);
};

// Date range options (Clover-style)
const DATE_RANGES = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'this_week', label: 'This week' },
  { value: 'last_week', label: 'Last week' },
  { value: 'last_7_days', label: 'Last 7 days' },
  { value: 'this_month', label: 'This month' },
  { value: 'last_month', label: 'Last month' },
  { value: 'custom', label: 'Custom range' }
];

// Payment status colors
const STATUS_COLORS = {
  paid: 'bg-green-100 text-green-800',
  pending: 'bg-yellow-100 text-yellow-800',
  failed: 'bg-red-100 text-red-800',
  refunded: 'bg-purple-100 text-purple-800',
  voided: 'bg-slate-100 text-slate-800'
};

// Sales Chart Component
function SalesChart({ data, height = 200 }) {
  const maxValue = Math.max(...data.map(d => d.value), 1);
  
  return (
    <div className="relative" style={{ height }}>
      <div className="absolute inset-0 flex items-end gap-1">
        {data.map((item, index) => {
          const barHeight = (item.value / maxValue) * 100;
          return (
            <div 
              key={index}
              className="flex-1 flex flex-col items-center justify-end"
            >
              <div 
                className="w-full bg-blue-500 rounded-t transition-all duration-300 hover:bg-blue-600"
                style={{ height: `${Math.max(barHeight, 2)}%` }}
              />
              <span className="text-[10px] text-slate-400 mt-1 truncate">{item.label}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function SalesPage() {
  const context = useOutletContext() || {};
  const { token = localStorage.getItem('backoffice_token'), manager = null, restaurant = null, activeRestaurantId = null, restaurants = [] } = context;
  const [searchParams, setSearchParams] = useSearchParams();
  
  // State
  const [dateRange, setDateRange] = useState(searchParams.get('range') || 'today');
  const [loading, setLoading] = useState(true);
  const [transactions, setTransactions] = useState([]);
  const [selectedTxn, setSelectedTxn] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  
  // Sales Summary State
  const [salesSummary, setSalesSummary] = useState({
    grossSales: 0,
    discounts: 0,
    refunds: 0,
    netSales: 0,
    taxes: 0,
    tips: 0,
    totalCollected: 0
  });
  
  // Chart Data
  const [chartData, setChartData] = useState([]);
  const [loadError, setLoadError] = useState(false);

  // Location filter
  const isMultiLocation = restaurants.length > 1;
  const selectedLocationName = restaurants.find(r => r.id === activeRestaurantId)?.name || restaurant?.name;

  const fetchSalesData = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      // Fetch sales data from API
      const response = await axios.get(`${API}/merchant/sales`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { 
          date_range: dateRange,
          restaurant_id: activeRestaurantId
        }
      });
      
      setTransactions(response.data.transactions || []);
      setSalesSummary(response.data.summary || {});
      setChartData(response.data.chart_data || []);
    } catch (error) {
      console.error('Sales fetch error:', error);
      toast.error('Could not load sales data');
      setLoadError(true);
      setTransactions([]);
      setChartData([]);
    } finally {
      setLoading(false);
    }
  }, [token, dateRange, activeRestaurantId]);

  useEffect(() => {
    if (token) {
      fetchSalesData();
    }
  }, [token, fetchSalesData]);

  const filteredTransactions = transactions.filter(txn => {
    if (filterStatus !== 'all' && txn.status !== filterStatus) return false;
    if (searchQuery && !txn.order_id?.toLowerCase().includes(searchQuery.toLowerCase()) &&
        !txn.customer_name?.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });

  const getDateRangeLabel = () => {
    return DATE_RANGES.find(r => r.value === dateRange)?.label || 'Today';
  };

  return (
    <div className="space-y-4 md:space-y-6" data-testid="sales-page">
      {/* Header - Stack on mobile */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-xl md:text-2xl font-bold text-gray-900">Sales</h1>
        
        {/* Date Range & Location Filter */}
        <div className="flex flex-wrap items-center gap-2">
          {/* Date Range Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                <span className="hidden sm:inline">{getDateRangeLabel()}</span>
                <span className="sm:hidden text-xs">{getDateRangeLabel()}</span>
                <ChevronDown className="h-3 w-3" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              {DATE_RANGES.map((range) => (
                <DropdownMenuItem 
                  key={range.value}
                  onClick={() => setDateRange(range.value)}
                  className={dateRange === range.value ? 'bg-blue-50' : ''}
                >
                  {range.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          
          {/* Location Chip */}
          {isMultiLocation && (
            <Badge variant="outline" className="px-2 py-1 text-xs font-medium bg-gray-100 hidden sm:flex">
              <Store className="h-3 w-3 mr-1" />
              {selectedLocationName}
            </Badge>
          )}

          {/* (a "Filters" button here opened nothing: the date range is the filter) */}
        </div>
      </div>

      {loadError && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 flex items-center justify-between gap-3" data-testid="sales-load-error">
          <span>Could not load sales data.</span>
          <Button variant="outline" size="sm" onClick={fetchSalesData}>Try again</Button>
        </div>
      )}

      {/* Net Sales Card with Chart */}
      <Card className="bg-white border-0 shadow-sm">
        <CardHeader className="pb-0 p-4 md:p-6">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base md:text-lg text-blue-600">Net sales</CardTitle>
              <p className="text-2xl md:text-4xl font-bold text-gray-900 mt-2">
                {formatCurrency(salesSummary.netSales)}
              </p>
            </div>
            <Button variant="ghost" size="sm" onClick={fetchSalesData}>
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="pt-4 md:pt-6 p-4 md:p-6">
          {chartData.length > 0 && (
            <SalesChart data={chartData} height={120} />
          )}
        </CardContent>
      </Card>

      {/* Sales Breakdown Table */}
      <Card className="bg-white border-0 shadow-sm overflow-hidden">
        <CardContent className="p-0">
          <Table>
            <TableBody>
              <TableRow className="hover:bg-gray-50">
                <TableCell className="font-medium text-gray-600 text-sm md:text-base">Gross sales</TableCell>
                <TableCell className="text-right font-semibold text-sm md:text-base">{formatCurrency(salesSummary.grossSales)}</TableCell>
              </TableRow>
              <TableRow className="hover:bg-gray-50">
                <TableCell className="font-medium text-gray-600 text-sm md:text-base">Discounts</TableCell>
                <TableCell className="text-right font-semibold text-red-600 text-sm md:text-base">-{formatCurrency(salesSummary.discounts)}</TableCell>
              </TableRow>
              <TableRow className="hover:bg-gray-50">
                <TableCell className="font-medium text-gray-600 text-sm md:text-base">Refunds</TableCell>
                <TableCell className="text-right font-semibold text-red-600 text-sm md:text-base">-{formatCurrency(salesSummary.refunds)}</TableCell>
              </TableRow>
              <TableRow className="bg-gray-50">
                <TableCell className="font-bold text-gray-800 text-sm md:text-base">Net sales</TableCell>
                <TableCell className="text-right font-bold text-base md:text-lg">{formatCurrency(salesSummary.netSales)}</TableCell>
              </TableRow>
              <TableRow className="hover:bg-gray-50">
                <TableCell className="font-medium text-gray-600 text-sm md:text-base">Taxes</TableCell>
                <TableCell className="text-right font-semibold text-sm md:text-base">{formatCurrency(salesSummary.taxes)}</TableCell>
              </TableRow>
              <TableRow className="hover:bg-gray-50">
                <TableCell className="font-medium text-gray-600 text-sm md:text-base">Tips</TableCell>
                <TableCell className="text-right font-semibold text-sm md:text-base">{formatCurrency(salesSummary.tips)}</TableCell>
              </TableRow>
              <TableRow className="bg-blue-50">
                <TableCell className="font-bold text-blue-800 text-sm md:text-base">Total collected</TableCell>
                <TableCell className="text-right font-bold text-base md:text-lg text-blue-800">{formatCurrency(salesSummary.totalCollected)}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Transactions Section */}
      <Card className="border-slate-200">
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <CardTitle>Transactions</CardTitle>
            <div className="flex flex-wrap items-center gap-2">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 w-48"
                />
              </div>
              
              {/* Status Filter */}
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-32">
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="refunded">Refunded</SelectItem>
                </SelectContent>
              </Select>

              {/* Export */}
              <Button variant="outline" size="sm">
                <Download className="h-4 w-4 mr-2" />
                Export
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Order</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Payment</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Time</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredTransactions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                      No transactions found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredTransactions.map((txn) => (
                    <TableRow key={txn.id} className="hover:bg-slate-50">
                      <TableCell className="font-medium">{txn.order_id}</TableCell>
                      <TableCell>{txn.customer_name || '-'}</TableCell>
                      <TableCell className="font-semibold">{formatCurrency(txn.amount)}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          {txn.payment_method === 'credit_card' ? (
                            <CreditCard className="h-4 w-4 text-blue-500" />
                          ) : (
                            <Banknote className="h-4 w-4 text-green-500" />
                          )}
                          <span className="text-sm capitalize">
                            {txn.payment_method?.replace('_', ' ') || 'Card'}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary" className={STATUS_COLORS[txn.status] || STATUS_COLORS.pending}>
                          {txn.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-slate-500 text-sm">
                        {new Date(txn.created_at).toLocaleTimeString('en-US', { 
                          hour: '2-digit', 
                          minute: '2-digit' 
                        })}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => setSelectedTxn(txn)}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Transaction Detail Dialog */}
      <Dialog open={!!selectedTxn} onOpenChange={() => setSelectedTxn(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="h-5 w-5" />
              Transaction Details
            </DialogTitle>
          </DialogHeader>
          
          {selectedTxn && (
            <div className="space-y-4 py-2">
              <div className="flex justify-between">
                <span className="text-slate-500">Order ID</span>
                <span className="font-medium">{selectedTxn.order_id}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Amount</span>
                <span className="font-bold text-lg">{formatCurrency(selectedTxn.amount)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Tax</span>
                <span className="font-medium">{formatCurrency(selectedTxn.tax)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Tip</span>
                <span className="font-medium">{formatCurrency(selectedTxn.tip)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Total</span>
                <span className="font-bold text-xl">{formatCurrency(selectedTxn.amount + (selectedTxn.tax || 0) + (selectedTxn.tip || 0))}</span>
              </div>
              <hr />
              <div className="flex justify-between">
                <span className="text-slate-500">Payment Method</span>
                <span className="font-medium capitalize">{selectedTxn.payment_method?.replace('_', ' ')}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Status</span>
                <Badge className={STATUS_COLORS[selectedTxn.status]}>{selectedTxn.status}</Badge>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Date & Time</span>
                <span className="font-medium">{new Date(selectedTxn.created_at).toLocaleString()}</span>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
